import { useState, useEffect, useRef, useMemo, SyntheticEvent } from 'react'
import { isEmpty } from 'lodash-es'
import DatePicker from 'react-datetime-picker'
import { useTranslation } from 'react-i18next'
import { isValid } from 'date-fns'
import { Modal } from 'semantic-ui-react'

import { isMidnight } from '@/helpers/date-helper'
import { useBreakpoints, useUserSetting } from '@/hooks'
import { Icon } from '@/ui'
import { SensitiveField, DatePickerWrapper } from './Styles'

interface CustomDatePickerProps {
  value: string | number | Date
  disabled?: boolean
  sensitive?: boolean
  onChange: (value: any) => void
  error?: boolean
  useTimePicker?: boolean
  keepTimezone?: boolean
  style?: object
  minDate?: Date | undefined
  customFormat?: any
  onSensitiveToggle?: (e: SyntheticEvent, toggled: boolean) => void
  clearable?: boolean
}

const formats: Record<string, string> = {
  en: 'M-d-y',
  en_GB: 'd-M-y',
  da: 'd-M-y',
  pl: 'd-M-y',
  de: 'd-M-y',
  es: 'd-M-y',
  fr: 'd-M-y',
  nb: 'd-M-y',
  nn: 'd-M-y',
  sv: 'y-M-d',
  it: 'd-M-y',
  nl: 'd-M-y',
  fi: 'd-M-y',
  ja: 'y年M月d日',
}

const CustomDatePicker = ({
  value,
  disabled,
  sensitive,
  onChange,
  error,
  useTimePicker,
  keepTimezone,
  style,
  minDate,
  customFormat,
  onSensitiveToggle,
  clearable = true,
}: CustomDatePickerProps) => {
  const { t, i18n } = useTranslation()
  const valueData = !isEmpty(value) ? new Date(value) : null
  const [{ value: preferredDateFormat }] = useUserSetting(
    'PREFERRED_DATE_FORMAT',
    'default',
    {
      fetchPolicy: 'cache-first',
    },
  )
  const [date, setDate] = useState<string | number | Date | null>(value)
  const [visible, setVisible] = useState(false)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  //Ref is some weird custom component with unknown properties
  const datePickerRef = useRef<any>(null)
  const { isMobile } = useBreakpoints()
  useEffect(() => {
    if (isMobile && !disabled) {
      const inputs: HTMLInputElement[] =
        datePickerRef.current?.wrapper?.querySelectorAll('input')
      console.log(inputs, 'inputs')
      if (inputs) {
        inputs.forEach(element => {
          element.disabled = true
        })

        return () => {
          inputs.forEach(element => {
            element.disabled = false
          })
        }
      }
    }
  }, [isMobile, disabled])

  useEffect(() => {
    setDate(value ? new Date(value) : null)
  }, [value])

  const handleChange = (date: Date | null) => {
    let newDate = null

    if (date) {
      if (useTimePicker || keepTimezone) {
        newDate = date.toISOString()
      } else {
        const yyyy = date.getFullYear().toString()
        const MM = (date.getMonth() + 1).toString().padStart(2, '0')
        const dd = date.getDate().toString().padStart(2, '0')
        newDate = `${yyyy}-${MM}-${dd}T00:00:00.000Z`
      }

      if (!isValid(new Date(newDate))) {
        return
      }
    }

    setDate(newDate)
    onChange(newDate)
  }

  let dateObject = date ? new Date(date) : valueData

  if (dateObject && !keepTimezone && isMidnight(dateObject)) {
    const [year, month, day] = dateObject
      .toISOString()
      .split('T')[0]
      .split('-')
      .map(datePart => parseInt(datePart))

    dateObject = new Date(year, month - 1, day)
  }

  const locale = i18n?.language || 'en'

  const format = useMemo(() => {
    if (customFormat) {
      return customFormat
    }

    const format =
      preferredDateFormat && preferredDateFormat !== 'default'
        ? preferredDateFormat.replace(/\//g, '-')
        : formats[locale] || formats.en

    if (useTimePicker) {
      return `${format} HH:mm`
    }

    return format
  }, [customFormat, preferredDateFormat, useTimePicker])

  const Input = (
    <>
      {isCalendarOpen && isMobile && (
        <Modal.Dimmer
          style={{
            zIndex: 9999,
          }}
        />
      )}

      <DatePickerWrapper
        style={style}
        disabled={disabled}
        sensitive={sensitive}
        onClick={() => {
          if (isMobile) {
            setIsCalendarOpen(true)
          }
        }}>
        <DatePicker
          disableClock
          //@ts-ignore
          ref={datePickerRef}
          onCalendarClose={() => setIsCalendarOpen(false)}
          onCalendarOpen={() => setIsCalendarOpen(true)}
          isCalendarOpen={isCalendarOpen}
          locale={locale.split('_')[0]}
          calendarIcon={<Icon name="icon-calendar" />}
          clearIcon={
            clearable && !disabled ? (
              <Icon
                name="icon-close-x"
                onClick={(e: SyntheticEvent) => {
                  console.log(e)
                  e.stopPropagation()
                  handleChange(null)
                }}
              />
            ) : null
          }
          disabled={disabled}
          dayPlaceholder={t('dd')}
          monthPlaceholder={t('mm')}
          yearPlaceholder={t('yyyy')}
          value={dateObject && isValid(dateObject) ? dateObject : undefined}
          format={format}
          onChange={handleChange}
          minDate={minDate}
          maxDate={new Date(9999, 12)}
          error={error}
        />
      </DatePickerWrapper>
    </>
  )

  if (sensitive) {
    return (
      <SensitiveField title={t('Edit content')}>
        {visible ? (
          Input
        ) : (
          <input
            disabled
            value="**********"
            style={{
              height: '35px',
              background: !disabled ? '#fff' : undefined,
            }}
          />
        )}

        <Icon
          button
          size="big"
          name={visible ? 'icon-sensitive-off' : 'icon-sensitive-on'}
          onClick={(e: SyntheticEvent) => {
            onSensitiveToggle?.(e, !visible)
            setVisible(!visible)
          }}
          style={{
            position: 'absolute',
            right: '8px',
            top: '8px',
          }}
        />
      </SensitiveField>
    )
  }

  return Input
}

export default CustomDatePicker
